import { makeStyles } from '@material-ui/core/styles'
import CheckCircle from '@material-ui/icons/CheckCircle'
import WarningIcon from '@material-ui/icons/Warning'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text, TypographyVariant } from 'shared/components/Text/Text'
import { GenericLoadStopProgress } from 'shared/models/job'
import {
  error,
  errorD1,
  kellyGreen,
  lightGray1,
  lightgreye3,
  marigoldOrange,
  midGray,
  paleyellow,
  success,
} from 'shared/theme/colors'

import { getFormattedDate, getFormattedDuration } from '../../helpers/formatters'
import { getJobTrackingStatusToDisplay } from '../../helpers/job'
import { LoadStopProgressStatus, TrackingStatus } from '../../models/load'

interface HosEtaSectionProps<T> {
  trackingStatus: TrackingStatus
  loadStopProgress: T
  titleVariant: TypographyVariant
  labelVariant: TypographyVariant
  textVariant: TypographyVariant
  className?: string
}

interface StylesProps {
  statusColor: string
  statusBackgroundColor: string
}

interface TrackingStatusBadgeProps<T> {
  hasArrivedAtStop: boolean
  trackingStatus: TrackingStatus
  loadStopProgress: T
  showHosEta?: boolean
}

const useStyles = makeStyles({
  trackingStatus: (props: StylesProps) => ({
    backgroundColor: props.statusBackgroundColor,
    padding: '5px',
    whiteSpace: 'nowrap',
    borderRadius: '3px',
  }),
  addressLine: {
    color: midGray,
  },
  etaIcon: (props: StylesProps) => ({
    color: props.statusColor,
  }),
  heading: {
    color: lightGray1,
    marginRight: '10px',
  },
  etaBreakdownWrapper: {
    marginLeft: '16px',
    '&:not(:last-child)': {
      borderLeft: `1px solid ${lightGray1}`,
    },
    '&:last-child > $etaSpacer': {
      borderLeft: `1px solid ${lightGray1}`,
    },
  },
  etaBreakdown: {
    padding: '5px',
  },
  etaSpacer: {
    height: '50%',
    width: '16px',
    borderBottom: `1px solid ${lightGray1}`,
    color: lightGray1,
  },
})

export function getTrackingStatusBackgroundColor(status: string) {
  switch (status) {
    case TrackingStatus.EARLY:
    case TrackingStatus.ON_TIME:
    case TrackingStatus.AT_FACILITY:
    case TrackingStatus.COMPLETED:
      return success
    case TrackingStatus.MAYBE_LATE:
      return paleyellow
    case TrackingStatus.LATE:
    case TrackingStatus.ERROR:
      return error
    default:
      return success
  }
}

export function getTrackingStatusTextColor(status: any) {
  switch (status) {
    case TrackingStatus.EARLY:
    case TrackingStatus.ON_TIME:
    case TrackingStatus.AT_FACILITY:
    case TrackingStatus.COMPLETED:
      return kellyGreen
    case TrackingStatus.MAYBE_LATE:
      return marigoldOrange
    case TrackingStatus.LATE:
    case TrackingStatus.ERROR:
      return errorD1
    default:
      return kellyGreen
  }
}

export const TrackingStatusBadge = <T extends GenericLoadStopProgress>({
  hasArrivedAtStop,
  trackingStatus,
  loadStopProgress,
  showHosEta = false,
}: TrackingStatusBadgeProps<T>) => {
  const statusToDisplay = getJobTrackingStatusToDisplay(trackingStatus, loadStopProgress, showHosEta)
  const statusBackgroundColor = getLoadStopProgressStatusBackgroundColor(loadStopProgress, trackingStatus)
  const statusTextColor = getLoadStopProgressStatusTextColor(loadStopProgress, trackingStatus)

  return (
    <FlexBox flexDirection="row" alignItems="flex-start" flex={2}>
      <FlexBox
        style={{
          backgroundColor: statusBackgroundColor,
          color: statusTextColor,
          padding: '5px',
          whiteSpace: 'nowrap',
          borderRadius: '3px',
        }}
      >
        {hasArrivedAtStop && <CheckCircle fontSize="small" />}
        <Text fontColor={statusTextColor} variant="paragraph2" fontWeight={400}>
          {statusToDisplay}
        </Text>
      </FlexBox>
    </FlexBox>
  )
}

export function getLoadStopProgressStatusBackgroundColor(loadStopProgress: any, trackingStatus: string) {
  if (!loadStopProgress) {
    return success
  }

  switch (loadStopProgress.status) {
    case LoadStopProgressStatus.SCHEDULED:
      return lightgreye3
    case LoadStopProgressStatus.EN_ROUTE:
      return getTrackingStatusBackgroundColor(trackingStatus)
    case LoadStopProgressStatus.AT_FACILITY:
    case LoadStopProgressStatus.DEPARTED:
      return success
    default:
      return success
  }
}

export function getLoadStopProgressStatusTextColor(loadStopProgress: any, trackingStatus: string) {
  if (!loadStopProgress) {
    return kellyGreen
  }

  switch (loadStopProgress.status) {
    case LoadStopProgressStatus.SCHEDULED:
      return midGray
    case LoadStopProgressStatus.EN_ROUTE:
      return getTrackingStatusTextColor(trackingStatus)
    case LoadStopProgressStatus.AT_FACILITY:
    case LoadStopProgressStatus.DEPARTED:
      return kellyGreen
    default:
      return kellyGreen
  }
}

export const HosEtaSection = <T extends GenericLoadStopProgress>({
  loadStopProgress,
  trackingStatus,
  titleVariant,
  labelVariant,
  textVariant,
  className,
}: HosEtaSectionProps<T>) => {
  const stylesProps = {
    statusColor: getLoadStopProgressStatusTextColor(loadStopProgress, trackingStatus),
    statusBackgroundColor: getLoadStopProgressStatusBackgroundColor(loadStopProgress, trackingStatus),
  }
  const classes = useStyles(stylesProps)

  const stop = loadStopProgress?.stop
  const showEtaIfValid = [
    TrackingStatus.EARLY,
    TrackingStatus.ON_TIME,
    TrackingStatus.MAYBE_LATE,
    TrackingStatus.LATE,
  ].includes(trackingStatus)
  const eta = showEtaIfValid
    ? getFormattedDate(loadStopProgress.lastScheduleEvent.driveDateEnd, stop.locationTimezone)
    : undefined

  return (
    <FlexBox flexDirection="column" className={className}>
      <FlexBox flexDirection="row" alignItems="center">
        <Text variant={titleVariant} style={{ marginRight: '10px' }} fontColor={lightGray1}>
          ETA
        </Text>
        <Text variant={textVariant} style={{ paddingLeft: '65px' }}>
          {eta || '-'}
        </Text>
      </FlexBox>
      {loadStopProgress.lastScheduleEvent.onDutyHours > 0 && eta && (
        <FlexBox flexDirection="row" className={classes.etaBreakdownWrapper}>
          <FlexBox className={classes.etaSpacer} />
          <FlexBox>
            <Text variant={labelVariant} className={classes.etaBreakdown} fontColor={lightGray1}>
              On duty
            </Text>
            <Text variant={textVariant} className={classes.etaBreakdown}>
              {getFormattedDuration(loadStopProgress.lastScheduleEvent.onDutyHours)}
            </Text>
          </FlexBox>
        </FlexBox>
      )}
      {loadStopProgress.lastScheduleEvent.offDutyHours > 0 && eta && (
        <FlexBox flexDirection="row" className={classes.etaBreakdownWrapper}>
          <FlexBox className={classes.etaSpacer} />
          <FlexBox>
            <Text variant={labelVariant} className={classes.etaBreakdown} fontColor={lightGray1}>
              Off duty
            </Text>
            <Text variant={textVariant} className={classes.etaBreakdown}>
              {getFormattedDuration(loadStopProgress.lastScheduleEvent.offDutyHours)}
            </Text>
          </FlexBox>
        </FlexBox>
      )}
    </FlexBox>
  )
}

export const HosETAWarning = () => {
  return (
    <FlexBox flexDirection="row" justifyContent="flex-start" alignItems="center">
      <FlexBox
        style={{
          backgroundColor: paleyellow,
          borderRadius: '50%',
          width: '30px',
          height: '30px',
          padding: '2px',
          alignItems: 'center',
          justifyContent: 'center',
          display: 'flex',
        }}
      >
        <WarningIcon fontSize="small" style={{ color: marigoldOrange }} />
      </FlexBox>
      <FlexBox style={{ paddingLeft: '5px' }}>
        <Text variant="label2">
          To avoid HOS violations, driver should take their required breaks, and off-duty time.
        </Text>
      </FlexBox>
    </FlexBox>
  )
}
